import React, { useMemo, useState } from "react";
import type { TooltipTriggerButtonProps } from "@design-system/overlay/triggerButtonProps";
import { Picker } from "@design-system/Picker/Picker.lib";
import { useWorkspaceTaskGroups } from "@api/taskGroup.api";
import { Task } from "@common/types/task";

interface TaskGroupSelectProps {
    value: Task["groupId"] | null | undefined;
    onChange: (id: Task["groupId"] | null) => void;
    /** A Picker trigger part (e.g. `Picker.CellContent`) replacing the default button. */
    trigger?: React.ReactNode;
    buttonProps?: TooltipTriggerButtonProps;
}

const NO_GROUP_LABEL = "No group";

const matchesSearch = (name: string, search: string) =>
    name.toLowerCase().includes(search);

export const TaskGroupSelect: React.FC<TaskGroupSelectProps> = ({
    value,
    onChange,
    trigger,
    buttonProps,
}) => {
    const { data: taskGroups = [], isFetched } = useWorkspaceTaskGroups();
    const [search, setSearch] = useState("");
    const {
        tooltip: triggerTooltip,
        variant: triggerVariant = "primary",
        ...triggerButtonRest
    } = buttonProps ?? {};
    const normalizedSearch = search.trim().toLowerCase();

    const visibleTaskGroups = useMemo(
        () =>
            normalizedSearch
                ? taskGroups.filter((taskGroup) =>
                      matchesSearch(taskGroup.name, normalizedSearch),
                  )
                : taskGroups,
        [taskGroups, normalizedSearch],
    );
    const isNoGroupVisible =
        !normalizedSearch || matchesSearch(NO_GROUP_LABEL, normalizedSearch);

    const selectedTaskGroupName = useMemo(() => {
        if (value === null) {
            return NO_GROUP_LABEL;
        }
        return taskGroups.find((taskGroup) => taskGroup.id === value)?.name;
    }, [taskGroups, value]);

    return (
        <Picker.Select
            searchable
            value={selectedTaskGroupName === undefined ? null : (value ?? null)}
            onValueChange={(groupId) => {
                if (groupId != null) {
                    onChange(groupId);
                }
            }}
            onInputValueChange={setSearch}
        >
            {trigger ?? (
                <Picker.ButtonTrigger
                    render={
                        <Picker.Button
                            block
                            {...triggerButtonRest}
                            variant={triggerVariant}
                            size={triggerButtonRest.size ?? "md"}
                        />
                    }
                    value={selectedTaskGroupName}
                    placeholder="Set group"
                    tooltip={triggerTooltip}
                />
            )}
            <Picker.Popup sideOffset={4}>
                <Picker.Search placeholder="Search task groups..." />
                <Picker.List>
                    {!isFetched && <Picker.Loading>Loading...</Picker.Loading>}
                    {isNoGroupVisible && (
                        <Picker.Item onSelect={() => onChange(null)}>
                            {NO_GROUP_LABEL}
                        </Picker.Item>
                    )}
                    {visibleTaskGroups.map((taskGroup) => (
                        <Picker.Item key={taskGroup.id} value={taskGroup.id}>
                            {taskGroup.name}
                        </Picker.Item>
                    ))}
                    {isFetched &&
                        !isNoGroupVisible &&
                        visibleTaskGroups.length === 0 && (
                            <Picker.Empty>No task groups</Picker.Empty>
                        )}
                </Picker.List>
            </Picker.Popup>
        </Picker.Select>
    );
};
